import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from "rxjs";
import { environment } from 'src/environments/environment';
import { SocketioService } from '../socket/socketio.service';

/**
 * Contrato de filtros del módulo "Reporte de Pagos a la Aseguradora".
 * Compartido por /list, /grouped y /stats — los tres deben recibir lo mismo para
 * que los KPIs no contradigan la tabla.
 */
export interface InsuranceReportFilters {
  status?: string;                 // pending | reported | failed | all
  search?: string;
  dateFrom?: string;               // YYYY-MM-DD
  dateTo?: string;                 // YYYY-MM-DD (inclusivo hasta 23:59:59.999 VET)
  dateField?: string;              // payment | register | reported
  paymentMethod?: string;          // ALL | DOMICILIATION | BDV_PAYMENT_BUTTON | ...
  bankCode?: string;
  reportedBy?: string;             // userId | SYSTEM
  minAttempts?: number;            // vista "a punto de fallar"
  sortOrder?: string;              // asc | desc
  page?: number;
  limit?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  auth$: BehaviorSubject<any> = new BehaviorSubject(false);

  showValidateTel$: BehaviorSubject<any> = new BehaviorSubject(false);
  tlfValidates$: BehaviorSubject<any> = new BehaviorSubject({});
  tlfValidatesWfReq$: BehaviorSubject<any> = new BehaviorSubject([]);
  nav$: BehaviorSubject<any> = new BehaviorSubject(true);
  take$: BehaviorSubject<any> = new BehaviorSubject(false);
  biometria$: BehaviorSubject<any> = new BehaviorSubject(null);

  private url_la: any;
  private url_OB: any;
  private url_TRUSTX: any;

  env: any = environment;
  private auth: any;
  private token: any;
  private user: any;
  private tokenName: any;
  constructor(public httpClient: HttpClient) {
    this.url_la = this.env.BASE_LA;
    this.url_OB = this.env.ONBOARDING_LA;
    this.url_TRUSTX = this.env.TRUSTX;
  }

  createUser = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/person/user`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  saveFee = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/fees`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  deleteFee = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.put<any>(`${this.baseUrl}/api/fees`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  changeStatusFee = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.put<any>(`${this.baseUrl}/api/fees/status`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  createPerson = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/person`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  saveTokenNotifications = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/save-token`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPersonByIdentifier = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/person/identity`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPersonbyCode = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/person/code`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Fuerza refresh de mora del cliente en el backend. Internamente el backend
  // hace recalcCustomerIfStale (idempotente: si está fresco no hace nada).
  // Usado por la consola antes de mostrar el módulo de pago.
  // force=true ignora el chequeo de stale y siempre recalcula — útil cuando
  // se sospecha desincronización (botón manual en el perfil).
  refreshCustomerMora = async (code: string, force: boolean = false) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/${code}/refresh_mora${force ? '?force=true' : ''}`,
          {},
          { headers }
        )
      );
    } catch (error) {
      console.error('refreshCustomerMora error:', error);
      throw error;
    }
  };

  reconciledPayment = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/reconciled_payment_invoces`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  cancelPayment = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/cancel_payment`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  paymentRollback = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/rollback_payment`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  getSection_transaction_by_person = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/person/section_transaction_by_person',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getInstallMents = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/installMents',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPeople_approved = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/people_approved', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPeople_status = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/approve/app_flow_statistics',
          {
            headers: headers,
            params: request,
          }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPeople = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/people', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getUsers = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/users', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPersonMaritalStatus = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/marital_status', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getBase_amount = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/base_amount', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  account_payment = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/account_payment',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  assignPaymentMethodsToCode = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/code/assign-payment-methods`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Obtener account payments filtrados por payment option
   * GET /api/invoice-credit/account_payment_by_payment_option?payment_option_id=
   */
  account_payment_by_payment_option = async (payment_option_id: string) => {
    const headers = { 'content-type': 'application/json' };
    const request = { payment_option_id };

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/account_payment_by_payment_option',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Obtener payment methods filtrados por payment option
   * GET /api/invoice-credit/payment_methods_by_payment_option?payment_option_id=
   */
  payment_methods_by_payment_option = async (payment_option_id: string) => {
    const headers = { 'content-type': 'application/json' };
    const request = { payment_option_id };

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/payment_methods_by_payment_option',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Actualizar el showClient de un payment method
   * PUT /api/invoice-credit/payment_method_status
   */
  updatePaymentMethodStatus = async (payment_method_id: string, showClient: boolean) => {
    const headers = { 'content-type': 'application/json' };
    const body = { payment_method_id, showClient };

    try {
      return lastValueFrom(
        this.httpClient.put<any>(
          this.baseUrl + '/api/invoice-credit/payment_method_status',
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  payment_methods = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/payment_method', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getSectionTransactions = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/person/section_transaction',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getSectionTransactionsProgres = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/section_transaction_progress`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPersonTypeIdentity = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/type_identity', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPersonCodeIdentity = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/person/code_identity', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  get baseUrl() {
    return this.url_la;
  }

  getDataBuro = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/buro/score`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  send_responsea_approvated = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/buro/send_responsea_approvated`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getExistBuro = async (body: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/buro/exist`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getScorePsicometrico = async (body: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/buro/psicometrico`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  setStatus_psicometrico = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/status_psicometrico`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  setStart_psicometrico = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/buro/start_psicometrico`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  clientQuota = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/client-quota`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  clientQuotaBasic = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/client-quota-basic`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // ============================================
  // SERVICIO: Listar Ejecutivos
  // ============================================
  getExecutivesList = async (searchQuery?: string) => {
    const headers = { 'content-type': 'application/json' };
    const params: any = {};

    if (searchQuery) {
      params.searchQuery = searchQuery;
    }

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/person/executives_list`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // ============================================
  // SERVICIO: Historial de Cambios de Cupo
  // ============================================
  getQuotaHistory = async (params: {
    codePerson?: string;
    codeExecutive?: string;
    fechaInicio?: string;
    fechaFin?: string;
    nombreEjecutivo?: string;
    page?: number;
    limit?: number;
    isGlobal?: boolean;
  }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/person/quota_history`,
          { headers: headers, params: params as any }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  clientCountCreditPending = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/client-count-credit-pending`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  creditSave = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/client-invoice-credit`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  clientQuotaReset = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/reset-credit-internal`,
          body,
          { headers: headers, params: request }
        )
      );

      // const resp = lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/api/invoice-credit/reset-invoice-all`, body, { headers: headers, params: request }));
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  clientQBuroReset = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/reset-buro-all`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  getSendsms = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `https://sms.creditotal.online/sendsms`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getVerificate = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`https://sms.creditotal.online/verificate`, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  sendsmsGeneral = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `https://sms.creditotal.online/sendsms/general`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  contact = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/person/contact`, body, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  verificatedEmailPost = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/email_verifcate`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  personApproveConditionsPost = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/approve_conditions`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  listClientInvoiceCredits = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/list_client_invoice_credits2`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getIsNeteable = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/is_netable`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  register_netable_payment = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/register_netable_payment`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  listInvoiceCredits = async (body: any = {}, request: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/list_invoice_credits`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  listClientPaymentsCredits = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/person_payments`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  listClientPaymentsCreditsConsole = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/person_payments_console`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Versión paginada + filtrable para la vista de tabla de pagos por persona.
  // body: { code_person, arr_typeProduct, type, reference, bank, amount_min, amount_max, account, sub_status, sortField, sortOrder }
  // query: { page, limit, init, end }  ->  devuelve { res, total, page, limit }
  listClientPaymentsConsolePaginated = async (body: any, query: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/person_payments_console_list`,
          body,
          { headers: headers, params: query }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPaymentByCode = async (payment_code: string, code_person: string) => {
    const headers = { 'content-type': 'application/json' };
    const body = { payment_code, code_person };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/person_payments`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error al consultar pago por código:', error);
      throw error;
    }
  };

  listPaymentsCredits = async (body: any = {}, request: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    try {
      const result = await lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/payments`,
          body,
          { headers: headers, params: request }
        )
      );
      return result;
    } catch (error) {
      console.error('Error en listPaymentsCredits:', error);
      throw error;
    }
  };

  getPersonInstallmentsInvoiceCredits = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/list_client_installment_invoice_credits`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // 📋 Obtener detalles de la póliza asociada a una compra financiada
  getPolicyByInvoice = async (invoiceCode: string) => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/insurance/policy_by_invoice`,
          { params: { invoice_code: invoiceCode } }
        )
      );
    } catch (error) {
      console.error('Error al obtener póliza:', error);
      throw error;
    }
  };

  getPersonInstallmentpayment = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/installment_client_payment`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getInstallMentsFN = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/installMentsFN',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getInstallMents_statusFN = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/installMents_statusFN',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getAllInvoicesReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/all_invoices_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  allPaymentsReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/all_payments_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  allFeesPaymentsReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/fees_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  allFeesPaymentsReportV2Get = async (params: any) => {

    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/invoice-credit/fees_report_v2`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  allFeesPaymentsReportV3Get = async (params: any) => {
    // Ajuste 18 — Reporte V3 Penalidades (12 columnas originales + 6 nuevas), 18 cols totales.
    // Lee fee_installments directo. Async via Trace_documet (type FEES_PAYMENTS_REPORT_V3).
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/invoice-credit/fees_report_v3`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };
  /* allFeesPaymentsReportV2Get
  
    allFeesPaymentsReportV2Get = async (params: any) => {
      try {
        return lastValueFrom(
          this.httpClient.get<any>(`${this.baseUrl}/api/report/fees_v2`, { params })
        );
      } catch (error) {
        console.error(error);
      }
    };
    */

  reactivationFeesStatusReportGet = async (params: any) => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/invoice-credit/reactivation_fees_status_report`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Ajuste 19 — Reporte 360 de Salud de Cartera (multi-pestaña + predictivo + NIIF 9)
  carteraHealth360Get = async () => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/invoice-credit/cartera_health_360`, {})
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_report_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/report`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_payments_report_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/payments-report`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_operative_report_caracas_v2_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/operative-report-caracas-v2`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_payments_report_sc_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/payments-report-sc`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_payments_report_sc10_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/payments-report-sc10`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_payments_report_sc11_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/payments-report-sc11`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_operative_report_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/operative-report`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Cartera de Seguros - Reporte de invoices de seguros
   * @param init - Fecha inicio (MM/dd/yyyy)
   * @param end - Fecha fin (MM/dd/yyyy)
   */
  insuranceInvoicesReportGet = async (init: any, end: any) => {
    try {
      const params = new HttpParams()
        .set('init', init)
        .set('end', end);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/reports/insurance/invoices`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte de Pagos Seguro Kredit
   * @param init - Fecha inicio (MM/dd/yyyy)
   * @param end - Fecha fin (MM/dd/yyyy)
   */
  insurancePaymentsReportGet = async (init: any, end: any) => {
    try {
      const params = new HttpParams()
        .set('init', init)
        .set('end', end);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/reports/insurance/payments`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte de Cuotas de Seguros
   * @param init - Fecha inicio (MM/dd/yyyy)
   * @param end - Fecha fin (MM/dd/yyyy)
   */
  insuranceInstallmentsReportGet = async (init: any, end: any) => {
    try {
      const params = new HttpParams()
        .set('init', init)
        .set('end', end);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/reports/insurance/installments`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_operative_report_caracas_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/operative-report-caracas`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_operative_report_sm_creditotal_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/operative-report-sm-creditotal`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_operative_report_sm_portal_externo_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/operative-report-sm-portal-externo`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  generate_insurance_operative_report_caracas11_csv = async (startDate: any, endDate: any) => {
    try {
      const params = new HttpParams()
        .set('startDate', startDate)
        .set('endDate', endDate);
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/insurance/operative-report-caracas-11`, { params })
      );
    } catch (error) {
      console.error(error);
    }
  };

  retryOrCreateInsuranceReport(paymentCode: string, dryRun: boolean = false) {
    const url = `${this.baseUrl}/api/admin/insurance-reports/retry-or-create/${paymentCode}` +
      (dryRun ? '?dryRun=true' : '');
    return this.httpClient.post<any>(url, {});
  }

  getInsuranceReportDetail(paymentCode: string) {
    return this.httpClient.get<any>(
      `${this.baseUrl}/api/admin/insurance-reports/detail/${paymentCode}`
    );
  }

  // === Consola de Reporte de Pagos de Seguros (admin) ===

  /**
   * Filtros del módulo. Los tres endpoints (list / grouped / stats) aceptan el
   * MISMO contrato: si divergen, los KPIs dejan de cuadrar con la tabla.
   *
   * dateField:
   *   'payment'  = fecha REAL del pago (register_payment_date ?? date) — default
   *   'register' = fecha de registro del pago
   *   'reported' = fecha en que se reportó a la aseguradora
   */
  private buildInsuranceReportParams(filters: InsuranceReportFilters = {}): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    });
    return params;
  }

  // Listado unificado (reportados + no reportados) con filtros/búsqueda/paginación
  listInsuranceReportPayments(filters: InsuranceReportFilters = {}) {
    const params = this.buildInsuranceReportParams(filters);
    return this.httpClient.get<any>(`${this.baseUrl}/api/admin/insurance-reports/list`, { params });
  }

  // Listado AGRUPADO por cliente → póliza (paginado por cliente)
  listInsuranceReportPaymentsGrouped(filters: InsuranceReportFilters = {}) {
    const params = this.buildInsuranceReportParams(filters);
    return this.httpClient.get<any>(`${this.baseUrl}/api/admin/insurance-reports/grouped`, { params });
  }

  /**
   * KPIs + facetas para los dropdowns (método, banco, reportado por) con contadores.
   * Recibe los MISMOS filtros que el listado: antes no aceptaba ninguno y las
   * tarjetas no se movían al filtrar, contradiciendo la tabla.
   */
  getInsuranceReportStats(filters: InsuranceReportFilters = {}) {
    const params = this.buildInsuranceReportParams(filters);
    return this.httpClient.get<any>(`${this.baseUrl}/api/admin/insurance-reports/stats`, { params });
  }

  // Traza de auditoría inmutable (quién hizo qué) de un pago
  getInsuranceReportAudit(paymentCode: string, page = 1, limit = 50) {
    const params = new HttpParams()
      .set('paymentCode', paymentCode)
      .set('page', String(page))
      .set('limit', String(limit));
    return this.httpClient.get<any>(`${this.baseUrl}/api/admin/insurance-reports/audit`, { params });
  }

  /**
   * Resumen agregado de auditoría: quién reportó cuánto, con qué resultado y cuándo.
   * OJO: los conteos son de INTENTOS, no de pagos (la colección guarda un registro
   * por intento). La respuesta lo declara en `counts_are`.
   */
  getInsuranceReportAuditSummary(filters: {
    dateFrom?: string; dateTo?: string; actorUserId?: string;
    result?: string; origin?: string; action?: string;
  } = {}) {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    });
    return this.httpClient.get<any>(`${this.baseUrl}/api/admin/insurance-reports/audit-summary`, { params });
  }

  allclientsReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/person/all_person_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };


  allclientsPlansReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/users-plans-report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  clientsdomicReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/person/accounts_person_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  dist_paymentsReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/all_approved_payments_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  report_biometricGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.url_OB + '/api/biometrics/report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  clientEstadisticReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/client_stadictic_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  sapPaymentsReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_payments_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  sapGirosReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_giros_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // 🆕 Reporte SAP Business Partner (datos maestros de clientes). Sin parámetros (universo completo).
  sapBusinessPartnerReportGet = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_business_partner_report',
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // 🆕 Reporte SAP de Partidas Abiertas (SALDOS INIC CRED T). Sin parámetros (piso fijo 17/12/2025).
  sapPartidasAbiertasReportGet = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_partidas_abiertas_report',
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // 🆕 Reporte SAP de Pérdidas Incobrables. Sin parámetros (todos los créditos <= 16/12/2025).
  sapPerdidasIncobrablesReportGet = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_perdidas_incobrables_report',
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte BDV BP Transacciones Encoladas
   */
  getBdvTransactionsReport = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/bdv_transactions_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * 📊 Reporte SAP Pagos BDV - Seguros
   * Genera reporte de pagos de seguros procesados por Banco de Venezuela
   * @param params.initDate - Fecha inicio (YYYY-MM-DD)
   * @param params.endDate - Fecha fin (YYYY-MM-DD)
   */
  sapPaymentsBdvReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_payments_bdv_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte SAP Pagos Mercantil - Seguros
   * Genera reporte de pagos de seguros procesados por Banco Mercantil
   * @param params.initDate - Fecha inicio (YYYY-MM-DD)
   * @param params.endDate - Fecha fin (YYYY-MM-DD)
   */
  sapPaymentsMercantilReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_payments_mercantil_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte SAP Pagos Banco Plaza - Seguros
   * Genera reporte de pagos de seguros procesados por Banco Plaza
   * @param params.initDate - Fecha inicio (YYYY-MM-DD)
   * @param params.endDate - Fecha fin (YYYY-MM-DD)
   */
  sapPaymentsBancoPlazaReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_payments_banco_plaza_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte SAP Pagos consolidado - Seguros Mercantil
   * Genera un archivo único con los pagos de los 3 bancos (BDV, Mercantil, Banco Plaza)
   * pertenecientes a la aseguradora Mercantil + 3 cabeceras (una por banco).
   */
  sapPaymentsMercantilInsurerReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_payments_mercantil_insurer_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 📊 Reporte SAP Pagos consolidado - Seguros Caracas
   * Genera un archivo único con los pagos de los 3 bancos (BDV, Mercantil, Banco Plaza)
   * pertenecientes a la aseguradora Caracas + 3 cabeceras (una por banco).
   */
  sapPaymentsCaracasInsurerReportGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/sap_payments_caracas_insurer_report',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  traceDocumentsGet = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/trace_documents',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // 🆕 2026-05-23 — Stats agregadas (avg duration por tipo) usadas para
  // mostrar "tiempo aprox basado en histórico" en cards PENDING.
  // ver docs/reportes/REPORT_CARD_REQUIREMENTS.md → "Tiempo aproximado smart"
  traceDocumentsStatsGet = async () => {
    try {
      return await lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/invoice-credit/trace_documents/stats`)
      );
    } catch (error) {
      console.error('traceDocumentsStatsGet error:', error);
      return { resp: {} };
    }
  };

  // 🆕 2026-05-23 — URL para exportar histórico XLSX (últimos N por tipo).
  // Devuelve la URL completa para que el componente haga el download.
  traceHistoryXlsxUrl = (keepPerType: number = 50): string => {
    return `${this.baseUrl}/api/invoice-credit/trace_documents/export-history.xlsx?keepPerType=${keepPerType}`;
  };

  // 🆕 2026-05-24 — Preservar / quitar preservación de un trace COMPLETE
  preserveTrace = async (traceId: string, name?: string | null) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return await lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/trace_documents/${traceId}/preserve`,
          name ? { name } : {},
          { headers }
        )
      );
    } catch (error: any) {
      console.error('preserveTrace error:', error);
      return { ok: false, msg: error?.error?.msg || 'Error al preservar' };
    }
  };

  unpreserveTrace = async (traceId: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return await lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/trace_documents/${traceId}/unpreserve`,
          {},
          { headers }
        )
      );
    } catch (error: any) {
      console.error('unpreserveTrace error:', error);
      return { ok: false, msg: error?.error?.msg || 'Error al quitar preservación' };
    }
  };

  // 🆕 2026-05-23 — Cancelar un reporte en ejecución (state=PENDING)
  // 🆕 2026-05-24 — Acepta razón opcional para auditoría
  cancelTraceDocument = async (traceId: string, reason?: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return await lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/trace_documents/${traceId}/cancel`,
          reason ? { reason } : {},
          { headers }
        )
      );
    } catch (error: any) {
      console.error('cancelTraceDocument error:', error);
      return { ok: false, msg: error?.error?.msg || 'Error al cancelar' };
    }
  };

  searchCatalogProducts = async (query: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/catalog-product/search', {
          headers: headers,
          params: { q: query },
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getshops = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/invoice-credit/shop', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  payment_sub_status = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/payment_sub_status',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getStadistic = async (body: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/stadistic`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  ejecutives_peoples = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/person/ejecutives_peoples',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getstadistic_invice_status = async (body: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/stadistic_invice_status`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getRegister_payment = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/register_payment`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Anula un pago que figura como conciliado pero sin distribución aplicada
   * (totalPaymentDue == amount, sin filas en InstallMent_payment / Fee_payment).
   * Solo OWNER. Marca el pago como Devolución de fondos.
   */
  cancelReconciledUndistributedPayment = async (body: any) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/cancel_reconciled_undistributed_payment`,
          body
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * Flujo especial: registra el pago pasando por todas las validaciones bancarias
   * pero sin conciliarlo. Soporta los modos REGISTER_NO_RECONCILE y
   * REGISTER_AND_ANNUL (este último anula el pago con la descripción recibida,
   * ej. "Devolución de fondos"). Solo perfil OWNER.
   */
  getRegister_payment_special = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/register_payment_special`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  getUpdate_payment = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/update_payment`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  setPersonBiometria = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/biometria`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getShowValidateTel = async () => {
    return this.showValidateTel$;
  };

  setTlfValidates(tlfValidates: any) {
    this.tlfValidates$.next(tlfValidates);
  }

  setNav(nav: any) {
    this.nav$.next(nav);
  }

  setTake(take: any) {
    this.take$.next(take);
  }

  setBiometria(biometria: any) {
    this.biometria$.next(biometria);
  }

  setShowValidateTel(showValidateTel: any) {
    this.showValidateTel$.next(showValidateTel);
  }

  getRate = async (body: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/rate`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  activeCupo = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/buro/active_cupo`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  manual_approve = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/approve/manual_approve`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  getDeduction_payment = async (body: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/deduction_payment`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  send_whatsapp = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/send_whatsapp`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  installMentNextCuote = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/installMent_next_cuote`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  invitarCompraAll = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/invitacion_compra_all`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  send_notification = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/send_notification`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  bulk_client_upload = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/bulk_client_upload`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  bulk_approved_upload = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/bulk_approved_upload`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  bulk_cupo_upload = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/bulk_cupo_upload`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  bulk_cupo_inactivate = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/bulk_cupo_inactivate`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };


  status_user = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/status_user`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  contact_user = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/contact_user`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  status_quote_user = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/toogle_status_quote_user`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  toogleStatusClient = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/toogle_client_status`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  shop_user = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/shop_user`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  profile_user = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/profile_user`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  pass_user = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/pass_user`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getBanks = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;
    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/invoice-credit/banks', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPaymentMethodCustomer = async (id: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/payment_method/customerById?id=' + id,
          { headers: headers, params: {} }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getAllies = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/alliesDirectDebit',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getBanksWithDirectDebitEnabled = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    //this.baseUrl + '/api/invoice-credit/banksDirectDebit',

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/banksDirectDebitConsole',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getActiveDirectDebits = async () => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/get_direct_debits',
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // `lote` busca por número de lote sin depender de la fecha: el backend ignora el filtro
  // de fecha cuando viene, porque justamente no se la sabe cuando se busca un lote puntual.
  getHistoricalDirectDebits = async (date?: string, bank?: string, type?: string, lote?: string, page = 1, limit = 20) => {
    const headers = { 'content-type': 'application/json' };
    const params: any = { page, limit };

    if (date) params.date = date;
    if (bank) params.bank = bank;
    if (type) params.type = type;
    if (lote) params.lote = lote;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/get_historical_direct_debits',
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /** Estadísticas de resultado de un lote: montos, cruce de conciliación y rechazos. */
  getDirectDebitStats = async (direct_debit_id: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/direct_debit_stats',
          { headers, params: { direct_debit_id } }
        )
      );
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  /**
   * Pago real detrás de una fila del detalle, con su distribución efectiva.
   * La llave son los DOS campos: el banco recicla las referencias entre lotes, así que
   * la referencia sola no identifica un pago.
   */
  getDirectDebitDetailPayment = async (direct_debit_id: string, reference: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/direct_debit_detail_payment',
          { headers, params: { direct_debit_id, reference } }
        )
      );
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  // `status_filter` acepta 'pending' | 'error' | 'completed'; vacío trae todos. El backend
  // clasifica igual que al contar, para que la tabla reproduzca el número de la tarjeta.
  getDirectDebitDetails = async (direct_debit_id: string, page: number = 1, limit: number = 100, search: string = '', status_filter: string = '') => {
    const headers = { 'content-type': 'application/json' };
    const params: any = { direct_debit_id, page: page.toString(), limit: limit.toString() };
    if (search) {
      params.search = search;
    }
    if (status_filter) {
      params.status_filter = status_filter;
    }

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/direct_debit_details',
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getDirectDebitTimeline = async (direct_debit_id: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = { direct_debit_id };

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/direct_debit_timeline',
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Obtiene el reporte de seguimiento de seguros para una domiciliación
   */
  getInsuranceReportTracking = async (direct_debit_id: string, filters?: any) => {
    const headers = { 'content-type': 'application/json' };
    const params: any = { direct_debit_id };
    
    if (filters) {
      if (filters.reportado !== undefined) params.reportado = filters.reportado;
      if (filters.limit) params.limit = filters.limit;
      if (filters.skip) params.skip = filters.skip;
    }

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/direct_debit/insurance-report-tracking',
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * Obtiene la URL para exportar el reporte de seguros a CSV
   */
  getInsuranceReportExportUrl = (direct_debit_id: string): string => {
    return `${this.baseUrl}/api/direct_debit/insurance-report-tracking/export?direct_debit_id=${direct_debit_id}`;
  };

  /**
   * 📊 Obtiene el progreso de conciliación en tiempo real
   * GET /api/direct_debit/conciliation_progress/:trace_id
   */
  getConciliationProgress = async (trace_id: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/direct_debit/conciliation_progress/${trace_id}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('❌ Error obteniendo progreso de conciliación:', error);
      throw error;
    }
  };

  getRemanentInvoice = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/installment_with_remainder`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  generatePagare = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/generate_pagare`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  getHistoryRates = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/history_rates',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  setHistoryRate = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/save_history_rate`,
          body,
          { headers: headers, params: request }
        )
      );
      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ Método dinámico para guardar tasas externas de cualquier canal
  saveExternalRate = async (amount: number, chanel: string, date?: string) => {
    const headers = { 'content-type': 'application/json' };
    const body: any = { amount, chanel };
    if (date) {
      body.date = date;
    }
    try {
      const resp = await lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/insurance/save_external_rate`,
          body,
          { headers: headers }
        )
      );
      return resp;
    } catch (error) {
      console.error('Error al guardar tasa externa:', error);
      throw error;
    }
  };

  // ✅ Método específico para cargar tasa de Seguros Mercantil (usa el servicio dinámico)
  loadMercantilExchangeRate = async (amount: number, date?: string) => {
    return this.saveExternalRate(amount, 'SEGUROS_MERCANTIL', date);
  };

  // ✅ Nuevo método para consultar tasa externa por fecha
  getExternalRateByDate = async (date: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = { date: date };
    try {
      const resp = await lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/insurance/external_rate_by_date`,
          { headers: headers, params: params }
        )
      );
      return resp;
    } catch (error) {
      console.error('Error al consultar tasa externa:', error);
      throw error;
    }
  };

  // ✅ Nuevo método para consultar tasas externas por rango de fechas
  getExternalRatesByRange = async (startDate: string, endDate: string, chanel?: string) => {
    const headers = { 'content-type': 'application/json' };
    const params: any = { startDate: startDate, endDate: endDate };
    if (chanel) {
      params.chanel = chanel;
    }
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/insurance/external_rates_by_range`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('Error al consultar tasas externas por rango:', error);
      throw error;
    }
  };

  getShowClientRate = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/get_show_client_rate`,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  updateShowClientRate = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/update_show_client_rate`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getShowMsRate = async () => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/get_show_ms_rate`,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  updateShowMsRate = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/update_show_ms_rate`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  createPlan = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/plan`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  deletePlan = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/plans/delete-plan`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  updateCustomerPlan = async (
    code: string,
    planId: string,
    action: 'add' | 'remove',
    id_ejecutive: string
  ) => {
    const headers = { 'Content-Type': 'application/json' };

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/person/update_customer_plans?code=${code}`,
          { code, planId, action, id_ejecutive }, // Payload containing both code and planId
          { headers } // Headers configuration
        )
      );
      return response.customerPlan; // Return the updated customer plan from the response
    } catch (error) {
      console.error('Error updating customer plan:', error);
      throw error;
    }
  };

  getAnalysis = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/approve/get_approve_history`,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getInReview = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/in-review`,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  approve_type_person = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/approve/approve_type_person`,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getManual_verification = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.url_OB}/api/biometrics/manual_verification`,

          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  setManual_verification = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.url_OB}/api/biometrics/manual_verification`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  setAnalysis = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/approve/creditotal_automatic_approve`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  update_quote_person = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/update_quote_person`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  activePlan = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/a-plan`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getFees = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getFeeByCode = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/code', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getFeeApplyByCode = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/applycode', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getListComparison = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_comparison', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getListFeeApplicationTypes = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_fee_application_types', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getListAccountPayments = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_account_payments', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getListFeeCaterories = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_categories', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };


  getListFeeMeasurements = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_fee_measurements', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };



  getListFeeAttributes = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_fee_attributes', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getListFeeFrecuencies = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/fees/list_fee_frecuencies', {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  getCustomerPlans = async (code: string) => {
    const headers = { 'content-type': 'application/json' };

    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/person/customer_plans?code=${code}`,
          {
            headers: headers,
          }
        )
      );
      return response.plans || [];
    } catch (error) {
      console.error('Error fetching customer plans:', error);
      throw error;
    }
  };

  updateDefaultPlan = async (code: string, planId: string) => {
    const headers = { 'content-type': 'application/json' };

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/person/update_default_plan`,
          { code, planId },
          { headers }
        )
      );
      return response;
    } catch (error) {
      console.error('Error updating default plan:', error);

      // Re-throw the error or return a rejected promise so it propagates correctly
      return Promise.reject(error);
    }
  };


  payment_sub_statusPost = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/payment_sub_status`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  customerDirectDebitProcesses = async (
    codeIdentity: string,
    identity: string,
    product_type: string = 'CREDIT'  // 🆕 Agregar product_type con default
  ) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl +
          '/api/direct_debit/direct_debit_status_client?identity=' +
          identity +
          '&codeIdentity=' +
          codeIdentity +
          '&product_type=' +  // 🆕 Agregar product_type al query
          product_type,
          { headers: headers, params: {} }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // 🆕 Servicio optimizado: obtiene todas las domiciliaciones activas en una sola llamada
  getAllCustomerDirectDebitProcesses = async (
    codeIdentity: string,
    identity: string
  ) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl +
          '/api/direct_debit/all_direct_debit_status_client?identity=' +
          identity +
          '&codeIdentity=' +
          codeIdentity,
          { headers: headers, params: {} }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  anularCompra = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    // El motivo viaja en el body; credit_code se mantiene como query param.
    const body = { reason: request?.reason || '' };
    const params = { credit_code: request?.credit_code };
    try {
      const resp = lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/pending_to_trash`,
          body,
          { headers: headers, params: params }
        )
      );

      return resp;
    } catch (error) {
      console.error(error);
    }
  };

  update_person = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/person/update_data_base`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };
  biometrics_data = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.url_OB}/api/biometrics/data`, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  biometrics_history = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.url_OB}/api/biometrics/history`, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  updateVerificationStatus = async (
    account_payment: string,
    typeConfirm: string
  ) => {
    const headers = { 'Content-Type': 'application/json' };

    try {
      const response = await lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/payment_method/change_status_verification_auto`,
          { account_payment, typeConfirm },
          { headers }
        )
      );
      return response;
    } catch (error) {
      console.error('Error updating validation:', error);
      throw error;
    }
  };

  updateAccountStatus = async (
    account_payment: string,
    showClient: boolean
  ) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    const body = {
      account_payment,
      showClient,
    };
    try {
      return lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/payment_method/update_account_visibility`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Actualizar la prioridad de una cuenta de pago
   */
  updateAccountPriority = async (
    paymentOptionId: string,
    accountId: string,
    action: 'UP' | 'DOWN'
  ) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    const body = {
      action,
    };
    try {
      return lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/payment-options/${paymentOptionId}/accounts/${accountId}/priority`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  clientStadistic = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/client-stadictic',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  getPersonImageRefresh = async (code_person: any, image_type: any) => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.url_OB +
          '/api/biometrics/user_img?person_code=' +
          code_person +
          '&image_type=' +
          image_type,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  personBiometricStatusRefresh = async (code_person: any) => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          this.url_OB + '/api/biometrics/status?person_code=' + code_person,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  enableBiometricPerson = async (body: any) => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          this.url_OB + '/api/data_update/enable_biometric',
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  historyManualVerification = async (request: any) => {
    const headers = { 'content-type': 'application/json' };
    const body = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.url_OB}/api/biometrics/manual_verification_history`,

          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }

  }


  getplansall = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {

      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/plans/all', {
          headers: headers,
          params: request,
        })

      );
    } catch (error) {
      console.error(error);
    }
  };



  getplansInvoices = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {

      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/plans/invoices_plan', {
          headers: headers,
          params: request,
        })

      );
    } catch (error) {
      console.error(error);
    }
  };


  getplans = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {

      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/plans/person', {
          headers: headers,
          params: request,
        })

      );
    } catch (error) {
      console.error(error);
    }
  };


  getPersonPrivate = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {

      return lastValueFrom(
        this.httpClient.get<any>(this.baseUrl + '/api/plans/person-private', {
          headers: headers,
          params: request,
        })

      );
    } catch (error) {
      console.error(error);
    }
  };



  allCodes = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/code`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };


  getAllCodes = async (params?: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/code',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };


  getTypeProducts = async (params?: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/invoice_product_code',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  updateCodeStatus = async (code: string, status: boolean) => {
    const headers = { 'Content-Type': 'application/json' };
    const body = { status };

    try {
      return lastValueFrom(
        this.httpClient.patch<any>(
          `${this.baseUrl}/api/code`,
          body,
          { headers: headers, params: { code } }
        )
      );
    } catch (error) {
      console.error('Error en la solicitud PATCH:', error);
      throw error;
    }
  };

  deleteCode = async (code: string) => {
    const headers = { 'Content-Type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.delete<any>(`${this.baseUrl}/api/code`, { headers: headers, params: { code } })
      );
    } catch (error) {
      console.error('Error en la solicitud DELETE:', error);
      throw error;
    }
  };


  createCodesPeople = async (body: any, additionalParam: any) => {
    const headers = { 'Content-Type': 'application/json' };
    const params = new HttpParams();

    try {
      const requestBody = { ...body, ...(additionalParam && { additionalParam }) };

      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/code/codes`,
          requestBody,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('Error en createCodesPeople:', error);
      throw error;
    }
  };

  deleteCodeForPerson = async (code: string, personCode?: string) => {
    const headers = { 'Content-Type': 'application/json' };

    // Crear parámetros incluyendo el personCode si se proporciona
    let params: any = { code };
    if (personCode) {
      params.personCode = personCode;
    }

    try {
      return lastValueFrom(
        this.httpClient.delete<any>(`${this.baseUrl}/api/code/codes`, {
          headers: headers,
          params: params
        })
      );
    } catch (error) {
      console.error('Error en la solicitud DELETE:', error);
      throw error;
    }
  };


  bulk_code_dynamic = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/integration/bulk_code_dynamic`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };


  setCodesProduct = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/invoice_product_code`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };


  getAllCodesProduct = async (params?: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/invoice_product_code',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  deleteCodeProduct = async (code: string) => {
    const headers = { 'Content-Type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.delete<any>(`${this.baseUrl}/api/invoice-credit/invoice_product_code`, { headers: headers, body: { code } })
      );
    } catch (error) {
      console.error('Error en la solicitud DELETE:', error);
      throw error;
    }
  };

  getTypesProductsInvoices = async () => {
    const headers = { 'content-type': 'application/json' };

    try {
      const result = await lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/get_type_product_invoice',
          { headers: headers }
        )
      );
      return result;
    } catch (error) {
      console.error('Error en getTypesProductsInvoices:', error);
      throw error;
    }
  };

  getProductsInvoicesFilter = async (params: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          this.baseUrl + '/api/invoice-credit/get_product_invoice_filter',
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  }; //Listo

  setProductsInvoices = async (body: any) => {
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/set_product_invoice`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  }; //Listo

  createProductsService = async (body: any) => {
    debugger;
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/create_product_invoice`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };//Listo

  deleteProductComplete = async (body: any) => {
    debugger;
    const headers = {};
    const request = {};

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/delete_product_invoice`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // New methods for dynamic codes management - REAL ENDPOINTS
  getDynamicCodesForProduct = async (productCode: string) => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/get_dinamic_codes?product_code=${productCode}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error getting dynamic codes for product:', error);
      throw error;
    }
  };

  associateDynamicCodeToProduct = async (body: any) => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/add_dinamic_code`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error associating dynamic code to product:', error);
      throw error;
    }
  };

  removeDynamicCodeFromProduct = async (body: any) => {
    const headers = { 'content-type': 'application/json' };

    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/remove_dinamic_code`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error removing dynamic code from product:', error);
      throw error;
    }
  };

  // Métodos para Remanente y Neteo
  setRemanenteNeteo = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/set_remanente_neteo`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  getRemanenteNeteo = async (productCode: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = { product_code: productCode };
    console.log('Solicitando configuración para producto:', productCode);
    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/get_remanente_neteo`,
          { headers: headers, params: params }
        )
      );
      console.log('Respuesta de configuración:', response);
      return response;
    } catch (error) {
      console.error('Error obteniendo configuración:', error);
      throw error;
    }
  };

  // Insurance configuration methods
  getInsurers = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/insurance/insurers`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  getInsurerProducts = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/insurance/insurer_products`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  setInsuranceConfig = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    console.log('Enviando configuración de seguros:', body);
    try {
      // Usar el mismo endpoint que maneja toda la configuración
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/set_remanente_neteo`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error en setInsuranceConfig:', error);
      throw error;
    }
  };

  // Métodos para Fees en Planes
  getAllFees = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/fees`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  addFeeToPlan = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/add_fee_to_plan`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.log(error)
      throw error;
    }
  };



  getInsuranceProposals = async (productCode: string, insuranceCode?: string) => {
    const headers = { 'content-type': 'application/json' };

    // Construir URL con parámetros
    let url = `${this.baseUrl}/api/insurance/console_proposals?product_code=${productCode}`;
    if (insuranceCode) {
      url += `&insurance_code=${insuranceCode}`;
    }

    console.log('🌐 Solicitando propuestas desde:', url);
    console.log('📋 Parámetros enviados:', {
      product_code: productCode,
      insurance_code: insuranceCode || 'no especificado'
    });

    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(
          url,
          { headers: headers }
        )
      );
      console.log('✅ Propuestas recibidas exitosamente');
      console.log('📦 Estructura completa de respuesta:', {
        type: typeof response,
        isArray: Array.isArray(response),
        keys: response ? Object.keys(response) : 'null',
        length: response && typeof response === 'object' ? Object.keys(response).length : 'N/A',
        hasResp: response && response.resp !== undefined,
        hasData: response && response.data !== undefined,
        sampleData: response && response.resp ? JSON.stringify(response.resp.slice(0, 2)) : 'N/A'
      });
      console.log('📦 Datos de respuesta:', JSON.stringify(response, null, 2));
      return response;
    } catch (error) {
      console.error('❌ Error obteniendo propuestas de seguros:', error);
      console.error('❌ URL que falló:', url);
      console.error('❌ Parámetros que fallaron:', {
        product_code: productCode,
        insurance_code: insuranceCode
      });
      throw error;
    }
  };

  getActiveProductsInvoices = async (domiActive?: boolean) => {
    const headers = { 'content-type': 'application/json' };
    const params: any = {};

    // Si domiActive es true, agregar el filtro
    if (domiActive === true) {
      params.domi_active = 'true';
    }

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/get_active_products_invoices`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };



  // Methods from incoming branch
  listPaymentsPendingAmount = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/analizar_fees?identity=${body.identity}`
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Obtiene las cuotas pendientes de un cliente específico
   * Adaptado del endpoint de la app móvil del cliente
   */
  getClientPendingQuotes = async (personCode: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/late_upcoming_inst_payments_jwt?personCode=${personCode}`,
          { headers }
        )
      );
    } catch (error) {
      console.error('Error fetching client pending quotes:', error);
      throw error;
    }
  };

  /**
   * Simulación: cómo se distribuirían los pagos por conciliar entre C001,
   * cuotas y C004 del cliente. Endpoint paralelo al anterior; si falla,
   * devuelve shape vacío para que la vista no se rompa.
   */
  getConciliarSimulation = async (personCode: string) => {
    try {
      return await lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/late_upcoming_conciliar_simulation?personCode=${personCode}`
        )
      );
    } catch (error) {
      console.error('Error fetching conciliar simulation:', error);
      return { payment_conciliar_total: 0, c001: [], installments: [], c004: [] };
    }
  };

  redistribuirPayment = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/invoice-credit/distribute_balance?codePayment=${body.code_payment}`
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  exonerarFee = async (body: any) => {
    const headers = {};
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/invoice-credit/desactivar_fees?id=${body.id}`,
          body,
          { headers: headers, params: request }
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Version Management Methods
  getVersionTimeline = async (codeConcepto: string) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/fees/versions/${codeConcepto}`, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  getVersionDetail = async (versionId: string) => {
    const headers = { 'content-type': 'application/json' };
    const request = {};
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/fees/version/${versionId}`, {
          headers: headers,
          params: request,
        })
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // Método para aplicar fee de reactivación (legacy)
  applyReactivationFee(personId: string) {
    const headers = { 'content-type': 'application/json' };
    return this.httpClient.post<any>(
      `${this.baseUrl}/api/test/fee-reactivation/apply/${personId}`,
      {},
      { headers: headers }
    );
  }

  // Validar si cliente puede generar fee C004
  validateReactivationStatus(personCode: string) {
    return this.httpClient.get<any>(
      `${this.baseUrl}/api/v1/reactivation/validate/${personCode}`
    );
  }

  // Generar fee C004 manualmente
  generateReactivationFee(personCode: string) {
    const headers = { 'content-type': 'application/json' };
    return this.httpClient.post<any>(
      `${this.baseUrl}/api/v1/reactivation/generate-fee`,
      { personCode },
      { headers }
    );
  }

  // ================================================================================
  // MÉTODOS BDV WORKFLOW VERIFICACIÓN - Flujo 2 Notificación de Pago Simplificado
  // ================================================================================

  /**
   * Verificar pago BDV con teléfono, monto, fecha y referencia opcional
   */
  verifyPaymentBDV = async (phone: string, amount: string, date: string, reference?: string): Promise<any> => {
    try {
      const body: any = {
        phone: phone,
        amount: amount,
        date: date
      };

      // Agregar referencia si se proporciona (caso de múltiples pagos)
      if (reference) {
        body.reference = reference;
      }

      console.log('[verifyPaymentBDV] Enviando:', body);

      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/invoice-credit/payments/verify`, body)
      );
    } catch (error) {
      console.error('[verifyPaymentBDV] Error:', error);
      throw error;
    }
  };

  /**
   * Confirmar pago BDV específico con referencia (opcional)
   */
  confirmPaymentBDV = async (phone: string, amount: string, reference?: string, date?: string): Promise<any> => {
    try {
      const body: any = {
        phone: phone,
        amount: amount
      };

      // Agregar referencia si se proporciona (caso de múltiples pagos)
      if (reference) {
        body.reference = reference;
      }

      // Agregar fecha si se proporciona
      if (date) {
        body.date = date;
      }

      console.log('[confirmPaymentBDV] Enviando:', body);

      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/invoice-credit/payments/confirm-bdv`, body)
      );
    } catch (error) {
      console.error('[confirmPaymentBDV] Error:', error);
      throw error;
    }
  };

  /**
   * Registrar pago con paymentToken del flujo BDV
   */
  registerPaymentWithToken = async (paymentData: any, paymentToken: string): Promise<any> => {
    try {
      // Agregar el paymentToken a los datos del pago
      const bodyWithToken = {
        ...paymentData,
        paymentToken: paymentToken
      };

      console.log('[registerPaymentWithToken] Enviando:', bodyWithToken);

      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/register-payment`, bodyWithToken)
      );
    } catch (error) {
      console.error('[registerPaymentWithToken] Error:', error);
      throw error;
    }
  };

  /**
   * Configurar account_payment con workflow BDV (para testing)
   */
  setAccountPaymentBDVWorkflow = async (accountPaymentCode: string): Promise<any> => {
    try {
      const body = {
        accountPaymentCode: accountPaymentCode
      };

      console.log('[setAccountPaymentBDVWorkflow] Configurando:', accountPaymentCode);

      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/account-payment/set-bdv-workflow`, body)
      );
    } catch (error) {
      console.error('[setAccountPaymentBDVWorkflow] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener la configuración actual del registro de usuarios
   */
  getRegistrationConfig = async (): Promise<any> => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/app-config/register/config`)
      );
    } catch (error) {
      console.error('[getRegistrationConfig] Error:', error);
      throw error;
    }
  };

  /**
   * Alternar el estado de habilitación del registro de usuarios
   * @param enable - true para habilitar, false para deshabilitar
   */
  toggleUserRegistration = async (enable: boolean): Promise<any> => {
    try {
      const body = { enable };

      console.log('[toggleUserRegistration] Cambiando estado a:', enable);

      return lastValueFrom(
        this.httpClient.put<any>(`${this.baseUrl}/api/app-config/register/toggle`, body)
      );
    } catch (error) {
      console.error('[toggleUserRegistration] Error:', error);
      throw error;
    }
  };

  /**
   * Actualizar el mensaje que se muestra cuando el registro está deshabilitado
   * @param text - Nuevo mensaje a mostrar
   */
  updateRegistrationMessage = async (text: string): Promise<any> => {
    try {
      const body = { text };

      console.log('[updateRegistrationMessage] Actualizando mensaje:', text);

      return lastValueFrom(
        this.httpClient.put<any>(`${this.baseUrl}/api/app-config/register/message`, body)
      );
    } catch (error) {
      console.error('[updateRegistrationMessage] Error:', error);
      throw error;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // 🆕 FASE 6: MÉTODOS MULTI-PRODUCTO
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener payment methods agrupados por producto
   * GET /api/payment_method/grouped_by_product?code_person=
   */
  getPaymentMethodsGrouped = async (params: { code_person: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/grouped_by_product`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[getPaymentMethodsGrouped] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener payment methods filtrados por producto
   * GET /api/payment_method/by_product?code_person=&product_type=
   */
  getPaymentMethodsByProduct = async (params: { code_person: string; product_type?: string; active_only?: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/by_product`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[getPaymentMethodsByProduct] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener afiliación activa por producto
   * GET /api/payment_method/afiliation_by_product?code_person=&product_type=
   */
  getAfiliationByProduct = async (params: { code_person: string; product_type?: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/afiliation_by_product`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[getAfiliationByProduct] Error:', error);
      throw error;
    }
  };

  /**
   * Verificar estado de domiciliación automática por producto
   * GET /api/payment_method/direct_debit_status_by_product?code_person=&product_type=
   */
  getDirectDebitStatusByProduct = async (params: { code_person: string; product_type?: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/direct_debit_status_by_product`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[getDirectDebitStatusByProduct] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener resumen de domiciliación por todos los productos
   * GET /api/payment_method/direct_debit_summary?code_person=
   */
  getDirectDebitSummary = async (params: { code_person: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/direct_debit_summary`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[getDirectDebitSummary] Error:', error);
      throw error;
    }
  };

  /**
   * Activar/desactivar domiciliación automática por producto
   * PUT /api/payment_method/toggle_automatic_by_product
   */
  toggleAutomaticByProduct = async (body: { code_person: string; product_type: string; automatic: boolean }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/payment_method/toggle_automatic_by_product`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[toggleAutomaticByProduct] Error:', error);
      throw error;
    }
  };

  /**
   * 🆕 Copiar payment_method_customer a otro producto
   * POST /api/payment_method/copy_to_product
   */
  copyPaymentMethodToProduct = async (body: { payment_method_customer_id: string; target_product_type: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/payment_method/copy_to_product`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[copyPaymentMethodToProduct] Error:', error);
      throw error;
    }
  };

  // 🆕 ========================================
  // TÉRMINOS Y CONDICIONES DE DOMICILIACIÓN POR PRODUCTO
  // ========================================

  /**
   * Obtener T&C activo de domiciliación para un producto
   * GET /api/terms_and_conditions_product/active/:productType
   */
  getActiveDomiciliationTerms = async (productType: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/active/${productType}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[getActiveDomiciliationTerms] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener T&C activo de domiciliación para un producto y banco específico
   * GET /api/terms_and_conditions_product/active/:productType/:bankCode
   */
  getActiveDomiciliationTermsByBank = async (productType: string, bankCode: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/active/${productType}/${bankCode}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[getActiveDomiciliationTermsByBank] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener historial de versiones de T&C para un producto
   * GET /api/terms_and_conditions_product/history/:productType
   */
  getTermsHistory = async (productType: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/history/${productType}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[getTermsHistory] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener historial de versiones de T&C para un producto y banco
   * GET /api/terms_and_conditions_product/history/:productType/:bankCode
   */
  getTermsHistoryByBank = async (productType: string, bankCode: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/history/${productType}/${bankCode}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[getTermsHistoryByBank] Error:', error);
      throw error;
    }
  };

  /**
   * Crear nuevo T&C de domiciliación para un producto y banco
   * POST /api/terms_and_conditions_product/create
   */
  createDomiciliationTerms = async (body: { product_type: string; bank_code: string; name: string; details: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/create`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[createDomiciliationTerms] Error:', error);
      throw error;
    }
  };

  /**
   * Editar T&C de domiciliación (crea nueva versión)
   * PUT /api/terms_and_conditions_product/edit
   */
  editDomiciliationTerms = async (body: { product_type: string; bank_code: string; name: string; details: string }) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/edit`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[editDomiciliationTerms] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener lista de productos con T&C de domiciliación configurados
   * GET /api/terms_and_conditions_product/products
   */
  getProductsWithDomiciliationTerms = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/products`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[getProductsWithDomiciliationTerms] Error:', error);
      throw error;
    }
  };

  /**
   * Verificar si un producto tiene T&C de domiciliación activos
   * GET /api/terms_and_conditions_product/check/:productType
   */
  checkDomiciliationTermsExist = async (productType: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/check/${productType}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[checkDomiciliationTermsExist] Error:', error);
      throw error;
    }
  };

  // ========================================
  // 🆕 MÉTODOS PARA SIMULADOR DE DOMICILIACIÓN DE SEGUROS
  // ========================================

  /**
   * Validar domiciliación para suscripción (4 casos)
   * GET /api/payment_method/validate_subscription_domiciliation
   */
  validateSubscriptionDomiciliation = async (code_person: string, product_type: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = new HttpParams()
      .set('code_person', code_person)
      .set('product_type', product_type);

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/validate_subscription_domiciliation`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[validateSubscriptionDomiciliation] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener T&C pendientes por aceptar
   * GET /api/payment_method/customerPendingTAC
   */
  getCustomerPendingTAC = async (code_person: string, product_type?: string) => {
    const headers = { 'content-type': 'application/json' };
    let params = new HttpParams().set('code_person', code_person);
    
    if (product_type) {
      params = params.set('product_type', product_type);
    }

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/customerPendingTAC`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[getCustomerPendingTAC] Error:', error);
      throw error;
    }
  };

  /**
   * Obtener T&C por payment method ID (endpoint simplificado)
   * GET /api/terms_and_conditions_product/by_payment_method/:paymentMethodCustomerId
   */
  getTermsByPaymentMethod = async (paymentMethodCustomerId: string) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/terms_and_conditions_product/by_payment_method/${paymentMethodCustomerId}`,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[getTermsByPaymentMethod] Error:', error);
      throw error;
    }
  };

  /**
   * Aceptar términos y condiciones de domiciliación
   * PUT /api/payment_method/direct_debit_status
   * 
   * El endpoint espera:
   * {
   *   "payment_method_customer": "6945a5ade9e5045c2b2bdb6f",
   *   "terms_and_condition": "65a580a3e0da26a180fd3e9c"
   * }
   */
  acceptDirectDebitTerms = async (paymentMethodCustomerId: string, termsAndConditionId: string) => {
    const headers = { 'content-type': 'application/json' };
    
    const body = {
      payment_method_customer: paymentMethodCustomerId,
      terms_and_condition: termsAndConditionId
    };

    try {
      return lastValueFrom(
        this.httpClient.put<any>(
          `${this.baseUrl}/api/payment_method/direct_debit_status`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[acceptDirectDebitTerms] Error:', error);
      throw error;
    }
  };

  /**
   * Crear recordatorio de domiciliación
   * POST /api/payment_method/remindme_later
   * 
   * Body:
   * {
   *   "person_code": "9de1596f-05a0-48fd-80f0-54e5a0bada41",
   *   "product_type": "SEGUROS_MERCANTIL"
   * }
   */
  createDomiciliationReminder = async (data: { person_code: string; product_type: string }) => {
    const headers = { 'content-type': 'application/json' };
    
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/payment_method/remindme_later`,
          data,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('[createDomiciliationReminder] Error:', error);
      throw error;
    }
  };

  /**
   * Verificar recordatorios pendientes de domiciliación
   * GET /api/payment_method/remindme_later?person_code=XXX&product_type=YYY
   * 
   * Marca automáticamente los recordatorios como notificados
   */
  checkDomiciliationReminders = async (person_code: string, product_type: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = new HttpParams()
      .set('person_code', person_code)
      .set('product_type', product_type);

    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/payment_method/remindme_later`,
          { headers: headers, params: params }
        )
      );
    } catch (error) {
      console.error('[checkDomiciliationReminders] Error:', error);
      throw error;
    }
  };
  /**
   * Descarga un archivo desde Cloudinary usando el endpoint del backend
   * Similar al método usado en direct_debit
   * @param url - URL completa del archivo de Cloudinary
   * @param extension - Extensión del archivo (pdf, jpg, png, etc.)
   */
  downloadFile(url: string, extension: string) {
    try {
      // Usar el endpoint del backend que maneja la descarga de Cloudinary
      return lastValueFrom(
        this.httpClient.get(`${this.baseUrl}/api/direct_debit/download?public_id=${url}&ext=${extension}`, { 
          responseType: 'blob' 
        })
      );
    } catch (error) {
      console.error('[downloadFile] Error:', error);
      return null;
    }
  }

  // ✅ MÉTODOS PARA SEGUROS CARACAS

  /**
   * Obtener configuración de visibilidad de tasa de Seguros Caracas
   */
  getShowScRate = async () => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/invoice-credit/get_show_sc_rate`, {
          headers: headers
        })
      );
    } catch (error) {
      console.error('[getShowScRate] Error:', error);
      throw error;
    }
  };

  /**
   * Actualizar configuración de visibilidad de tasa de Seguros Caracas
   */
  updateShowScRate = async (body: any) => {
    const headers = { 'content-type': 'application/json' };
    try {
      return lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/invoice-credit/update_show_sc_rate`, body, {
          headers: headers
        })
      );
    } catch (error) {
      console.error('[updateShowScRate] Error:', error);
      throw error;
    }
  };

  // ============================================================
  // MÉTODOS PARA CARGA MASIVA C004
  // ============================================================

  downloadC004Template = async () => {
    try {
      return await lastValueFrom(
        this.httpClient.get(`${this.baseUrl}/api/v1/c004-batch/template`, {
          responseType: 'blob'
        })
      );
    } catch (error) {
      console.error('[downloadC004Template] Error:', error);
      throw error;
    }
  };

  validateC004File = async (formData: FormData) => {
    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/v1/c004-batch/validate`, formData)
      );
      return response;
    } catch (error) {
      console.error('[validateC004File] Error:', error);
      throw error;
    }
  };

  uploadC004Batch = async (formData: FormData) => {
    try {
      const response = await lastValueFrom(
        this.httpClient.post<any>(`${this.baseUrl}/api/v1/c004-batch/upload`, formData)
      );
      return response;
    } catch (error) {
      console.error('[uploadC004Batch] Error:', error);
      throw error;
    }
  };

  getC004BatchDetail = async (batchId: string) => {
    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/v1/c004-batch/${batchId}`)
      );
      return response.batch;
    } catch (error) {
      console.error('[getC004BatchDetail] Error:', error);
      throw error;
    }
  };

  getC004BatchList = async (filters: any) => {
    let params = new HttpParams();
    
    if (filters.page) params = params.set('page', filters.page);
    if (filters.limit) params = params.set('limit', filters.limit);
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    if (filters.userId) params = params.set('userId', filters.userId);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.batchNumber) params = params.set('batchNumber', filters.batchNumber);

    try {
      const response = await lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/v1/c004-batch/list`, {
          params: params
        })
      );
      return response;
    } catch (error) {
      console.error('[getC004BatchList] Error:', error);
      throw error;
    }
  };

  // Ruta ADMIN: la consola es un operador mirando la ficha de OTRA persona, así
  // que `personCode` es el del cliente en pantalla, no el del token. La ruta
  // pública `/api/rewards/summary/:personCode` compara ambos y responde 403
  // —correcto para la app del cliente, inservible acá—.
  getRewardsSummary = async (personCode: string) => {
    try {
      return await lastValueFrom(
        this.httpClient.get<any>(`${this.baseUrl}/api/rewards/admin/summary/${personCode}`)
      );
    } catch (error) {
      console.error('[rewards]', error);
      return { success: true, data: null };
    }
  };

  // ==========================================
  // BDV Consultas
  // ==========================================

  bdvConsultarMovimientos = async (body: any) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv-consultas/movimientos`,
          body
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvConsultarSaldo = async (body: any) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv-consultas/saldo`,
          body
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBuscarNotificacion = async (body: any) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv-consultas/buscar-notificacion`,
          body
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvConsultarBitacora = async (body: any) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv-consultas/bitacora`,
          body
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // ==========================================
  // BDV Botón de Pagos — Conciliación (consola ejecutivos)
  // ==========================================

  bdvBPConsultarTx = async (txNumber: number) => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/consultar-tx/${txNumber}`
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPConsultarBatch = async (transactionNumbers: number[]) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/consultar-tx-batch`,
          { transactionNumbers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPRegistrarPagos = async (transactionNumbers: number[]) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/registrar-pagos`,
          { transactionNumbers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPVerificarBanco = async (txNumber: number) => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/verificar-banco/${txNumber}`
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPDescartarTx = async (txNumber: number, reason?: string, bankResult?: string) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/descartar-tx/${txNumber}`,
          { reason, bankResult }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPSimularRegistro = async (transactionNumbers: number[]) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/simular-registro`,
          { transactionNumbers }
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPConsultarBitacora = async (txNumber: number) => {
    try {
      return lastValueFrom(
        this.httpClient.get<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/bitacora/${txNumber}`
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  bdvBPBuscarFiltro = async (filtro: any) => {
    try {
      return lastValueFrom(
        this.httpClient.post<any>(
          `${this.baseUrl}/api/bdv_payment_button/conciliacion/buscar`,
          filtro
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

}
